import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { apiErrorMessage } from 'i18n/apiError'
import { useChineseLang } from '../../i18n/chinese'
import { converterWord } from 'utils/helperFunction'
import { characterConst } from 'utils/constant'
import { answerValidate } from 'utils/utils'
import { hapticSelection, hapticSuccess } from 'utils/haptics'
import { trackWriteSpeak } from 'utils/analytics'
import { LiveSession, startLiveTranscription } from 'utils/liveTranscription'
import { useFeatureVideoCallout } from 'containers/feature-video-callout/FeatureVideoCallout'
import { uploadContentTypeFor } from 'shared/useRecorder'
import { prefersReducedMotion } from 'shared/useMicLevels'
import { createRecordingUploadUrl, uploadRecordingToPresignedUrl } from '../../redux/actions/tasterAction'
import { reportTranscribeUsage, requestTranscribeToken, submitWriteSpeak } from '../../redux/actions/writeSpeakAction'
import {
  PracticeWord,
  SpeakingModeId,
  WordHeard,
  WriteSpeakState,
  WriteSpeakSubmitPayload,
} from 'types/writeSpeakTypes'
import {
  allWordsUsedLabel,
  countCharacters,
  liveWordsFrom,
  localTime,
  MODE_TAKE_LABEL,
  resolveSendVariant,
  segmentText,
  SendVariant,
  splitParagraphs,
  tierFromPlanName,
  variantCanSend,
  variantIsFinal,
  writtenWords,
} from './logic'
import WordsToTry, { isTicked, TypingStrip, WordsList, WordTick } from './WordsToTry'
import { ModePicker, Script } from './SpeakingModes'
import StepRecorder, { StepRecorderHandle, StopReason, Take } from './StepRecorder'
import SendArea, { InfoIcon, SendPhase } from './SendArea'
import Receipt from './Receipt'
import OthersAnswers, { OtherAnswer } from './OthersAnswers'
import ZhWords, { ClickWordHandler } from './ZhText'
import './writeSpeak.css'

const KEYBOARD_GUIDE_URL = 'https://www.maayot.com/blog/chinese-keyboard-and-how-to-type-in-chinese/'
const UPSELL_CALLOUT_KEY = 'write_speak_upsell_callout_dismissed'
const TICK_DEBOUNCE_MS = 120
/** A word first shows up in a partial 0.6 to 1.4 s after it is spoken (measured 2026-09-29). */
const TRANSCRIPTION_LAG_S = 1.2
// Not copy: the answer box's hint is Chinese on purpose, it shows the learner what to type.
const ANSWER_PLACEHOLDER_ZH = '用中文写你的回答…'
// Not copy: the Chinese title marks around the story the day's correction went to.
const CAP_TITLE_OPEN = '《'
const CAP_TITLE_CLOSE = '》'

const draftKey = (memberId: string, storyId: string) => `ws_draft_${memberId}_${storyId}`

const readDraft = (memberId: string, storyId: string): string => {
  if (!memberId) return ''
  try {
    return localStorage.getItem(draftKey(memberId, storyId)) || ''
  } catch {
    return ''
  }
}

const writeDraft = (memberId: string, storyId: string, text: string) => {
  if (!memberId) return
  try {
    if (text) localStorage.setItem(draftKey(memberId, storyId), text)
    else localStorage.removeItem(draftKey(memberId, storyId))
  } catch {
    /* no storage: the draft just doesn't survive a reload */
  }
}

const calloutDismissed = (key: string) => {
  try {
    return localStorage.getItem(key) === 'true'
  } catch {
    return true
  }
}

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const usePhone = () => {
  const query = '(max-width: 640px)'
  const [isPhone, setIsPhone] = useState(() => {
    try {
      return window.matchMedia(query).matches
    } catch {
      return false
    }
  })
  useEffect(() => {
    let mql: MediaQueryList
    try {
      mql = window.matchMedia(query)
    } catch {
      return
    }
    const onChange = () => setIsPhone(mql.matches)
    mql.addEventListener?.('change', onChange)
    return () => mql.removeEventListener?.('change', onChange)
  }, [])
  return isPhone
}

/** Nearest ancestor that scrolls (the app scrolls inside .scroll_contain_div on desktop). */
const scrollParentHeight = (el: HTMLElement | null): number => {
  let node = el?.parentElement || null
  while (node && node !== document.body) {
    const style = window.getComputedStyle(node)
    if (/(auto|scroll)/.test(`${style.overflowY}${style.overflow}`) && node.scrollHeight > node.clientHeight) {
      return node.clientHeight
    }
    node = node.parentElement
  }
  return window.innerHeight
}

export interface WriteSpeakStepProps {
  storyId: string
  level: string
  memberId: string
  memberName: string
  planName?: string
  isStandard: boolean
  isStandardTrial: boolean
  story: any
  questionContent: any[]
  curAnswer: string
  wsState: WriteSpeakState | null
  wsStateLoaded: boolean
  words: PracticeWord[]
  answers: OtherAnswer[]
  answersTotal: number
  answersLoading: boolean
  onLoadAllAnswers: () => void
  onAnswerSaved: (answer: string) => void
  activeIndex: string
  onClickWord: ClickWordHandler
  onInfoWord: (word: PracticeWord, event: React.MouseEvent) => void
  activeInfoWord: string | null
  known: Map<string, { pinyin?: string; english?: string }>
  characterPreference?: string
  onUnsentChange: (unsent: boolean) => void
  /** Replaces "How other learners answered" (lesson discussions); shown under the same rule, after sending. */
  renderOthers?: () => React.ReactNode
}

const WriteSpeakStep: React.FC<WriteSpeakStepProps> = (props) => {
  const {
    storyId,
    level,
    memberId,
    memberName,
    planName,
    isStandard,
    isStandardTrial,
    story,
    questionContent,
    curAnswer,
    wsState,
    wsStateLoaded,
    words,
    answers,
    answersTotal,
    answersLoading,
    onLoadAllAnswers,
    onAnswerSaved,
    activeIndex,
    onClickWord,
    onInfoWord,
    activeInfoWord,
    known,
    characterPreference,
    onUnsentChange,
    renderOthers,
  } = props
  const { t } = useTranslation('writeSpeak')
  const zhLang = useChineseLang()
  const isPhone = usePhone()
  const reduced = useMemo(prefersReducedMotion, [])

  // ---------- plan ----------
  const tier = wsState?.tier || tierFromPlanName(planName)
  const [variantOverride, setVariantOverride] = useState<SendVariant | null>(null)
  const variant: SendVariant = variantOverride || resolveSendVariant(tier, wsState)
  const existing = wsState?.existing || null
  const isProTier = tier === 'pro'
  const canSendNow = variantCanSend(variant)
  const isFinal = variantIsFinal(variant)
  // Words said out loud reach the teacher with Pro and with the free correction alike.
  const hearsWords = isProTier || variant === 'taster'

  // ---------- writing ----------
  const [answer, setAnswer] = useState(() => readDraft(memberId, storyId) || curAnswer || '')
  const [matchText, setMatchText] = useState(answer)
  const [touched, setTouched] = useState(false)
  const [draftSaved, setDraftSaved] = useState(!!answer)
  const [writingDone, setWritingDone] = useState(!!answer.trim())
  const [editingExisting, setEditingExisting] = useState(false)
  const [fieldError, setFieldError] = useState('')
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const composingRef = useRef(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // The saved answer arrives with the state request, and on a reload the member id can land after
  // this mounts (the question is persisted, credentials aren't), so the draft may only be readable
  // now: fill the box if the learner hasn't started.
  useEffect(() => {
    if (touched || answer) return
    const draft = readDraft(memberId, storyId)
    const saved = draft || existing?.answer || curAnswer
    if (!saved) return
    setAnswer(saved)
    setMatchText(saved)
    setWritingDone(true)
    if (draft) setDraftSaved(true)
  }, [memberId, existing?.answer, curAnswer])

  // Draft autosave, per story, on this device only.
  useEffect(() => {
    if (!touched) return
    setDraftSaved(false)
    const id = setTimeout(() => {
      writeDraft(memberId, storyId, answer)
      setDraftSaved(true)
    }, 400)
    return () => clearTimeout(id)
  }, [answer, touched])

  const runMatch = (text: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setMatchText(text), TICK_DEBOUNCE_MS)
  }

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
      document.body.classList.remove('ws-typing')
    },
    [],
  )

  const onAnswerChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAnswer(e.target.value)
    setTouched(true)
    if (fieldError) setFieldError('')
    // Never match mid-composition: half-typed pinyin would flicker the ticks.
    if (!composingRef.current) runMatch(e.target.value)
  }

  const trimmed = answer.trim()
  const hasText = trimmed.length > 0
  const existingAnswer = (existing?.answer || '').trim()
  const writingReadOnly = isFinal || (variant === 'open' && !!existingAnswer && !editingExisting)

  // ---------- words to use ----------
  const liveWords = useMemo(() => liveWordsFrom(words, (w) => converterWord(w, characterConst.traditional)), [words])
  const written = useMemo(() => writtenWords(matchText, liveWords), [matchText, liveWords])
  const [heard, setHeard] = useState<Record<string, number>>({})
  const [manual, setManual] = useState<Record<string, boolean>>({})
  const ticks = useMemo(() => {
    const out: Record<string, WordTick> = {}
    words.forEach((w) => {
      out[w.word] = {
        written: written.indexOf(w.word) !== -1,
        heard: heard[w.word] !== undefined,
        manual: !!manual[w.word],
      }
    })
    return out
  }, [words, written, heard, manual])
  const tickedCount = words.filter((w) => isTicked(ticks[w.word])).length
  const allTicked = words.length > 0 && tickedCount === words.length
  // Read when the microphone opens, which is after the render that follows a Record again.
  const allTickedRef = useRef(allTicked)
  allTickedRef.current = allTicked

  const [sent, setSent] = useState(false)
  const locked = sent || isFinal

  const onToggleWord = useCallback(
    (word: string) => {
      if (locked) return
      const t = ticks[word]
      // A word ticked by writing or speech stays ticked; a tap only adds or removes the learner's own tick.
      if (!t || t.written || t.heard) return
      hapticSelection()
      setManual((prev) => ({ ...prev, [word]: !prev[word] }))
    },
    [ticks, locked],
  )

  // ---------- speaking ----------
  const [mode, setMode] = useState<SpeakingModeId>('read_passage')
  const paragraphs = useMemo(() => splitParagraphs<any>(story?.storyContent), [story?.storyContent])
  const [para, setPara] = useState(0)
  const [take, setTake] = useState<Take | null>(null)
  const [takeMeta, setTakeMeta] = useState<{ mode: SpeakingModeId; text: string; para: number } | null>(null)
  const [recording, setRecording] = useState(false)
  const [nudged, setNudged] = useState(false)
  const [liveFailed, setLiveFailed] = useState(false)
  const recorderRef = useRef<StepRecorderHandle>(null)
  const pendingMetaRef = useRef<{ mode: SpeakingModeId; text: string; para: number; reason: StopReason } | null>(null)
  const liveRef = useRef<Promise<LiveSession | null> | null>(null)
  const recordStartRef = useRef(0)

  const liveOn = hearsWords && !!wsState?.liveTicking && !liveFailed
  const holdsWritingTake = !!take && takeMeta?.mode === 'read_writing'

  useEffect(() => {
    if (!hasText && mode === 'read_writing' && !holdsWritingTake) setMode('read_passage')
  }, [hasText, mode, holdsWritingTake])

  const stopLive = () => {
    const pending = liveRef.current
    liveRef.current = null
    pending?.then((session) => session?.stop()).catch(() => undefined)
  }

  useEffect(() => () => stopLive(), [])

  const onRecordingStarted = (stream: MediaStream, startedAt: number) => {
    recordStartRef.current = startedAt
    setRecording(true)
    setNudged(true)
    trackWriteSpeak('recording_started', { mode, tier })
    // Listen in every mode, so the teacher also gets 'Said' timestamps when reading aloud.
    // Nothing left to tick (the writing already used every word): don't listen at all.
    if (liveOn && liveWords.length && !allTickedRef.current) {
      liveRef.current = startLiveTranscription({
        stream,
        words: liveWords,
        fetchToken: requestTranscribeToken,
        reportUsage: reportTranscribeUsage,
        onHeard: (word) => {
          const at = Math.max(0, (performance.now() - recordStartRef.current) / 1000 - TRANSCRIPTION_LAG_S)
          setHeard((prev) => (prev[word] !== undefined ? prev : { ...prev, [word]: Math.round(at * 10) / 10 }))
        },
        onFail: () => setLiveFailed(true),
      })
        .catch(() => null)
        .then((session) => {
          // No session (no token, no AudioWorklet): stop promising auto-ticks and fall back to tapping.
          if (!session) setLiveFailed(true)
          return session
        })
    }
  }

  // Every word ticked mid-take: stop listening, there is nothing left to hear.
  useEffect(() => {
    if (recording && allTicked) stopLive()
  }, [recording, allTicked])

  const onRecordingStopped = (reason: StopReason) => {
    setRecording(false)
    stopLive()
    pendingMetaRef.current = { mode, text: trimmed, para, reason }
  }

  // ---------- background upload ----------
  const uploadRef = useRef<{ url: string; promise: Promise<string> } | null>(null)

  const uploadOnce = async (t: Take): Promise<string> => {
    const contentType = uploadContentTypeFor(t.mime)
    const grant = await createRecordingUploadUrl(storyId, contentType)
    await uploadRecordingToPresignedUrl(grant.uploadUrl, t.file, contentType)
    return grant.key
  }

  const uploadWithRetry = async (t: Take): Promise<string> => {
    const backoff = [0, 1000, 3000]
    let lastError: any
    for (let i = 0; i < backoff.length; i++) {
      if (backoff[i]) await wait(backoff[i])
      try {
        return await uploadOnce(t)
      } catch (err) {
        lastError = err
      }
    }
    throw lastError
  }

  const startUpload = (t: Take) => {
    const promise = uploadWithRetry(t)
    promise.catch(() => undefined) // surfaced on send, not before
    uploadRef.current = { url: t.url, promise }
  }

  const ensureUploaded = async (t: Take): Promise<string> => {
    if (!uploadRef.current || uploadRef.current.url !== t.url) startUpload(t)
    try {
      return await (uploadRef.current as { promise: Promise<string> }).promise
    } catch {
      startUpload(t)
      return (uploadRef.current as { promise: Promise<string> }).promise
    }
  }

  // Upsell callout for learners who can't send: opens by itself once, right after
  // their first recording, for Free and Standard trial; only on tap for paying Standard.
  const upsell = useFeatureVideoCallout(UPSELL_CALLOUT_KEY, { autoOpen: false })
  const upsellAutoEligible = !isStandard || isStandardTrial

  const onTake = (t: Take) => {
    const meta = pendingMetaRef.current || { mode, text: trimmed, para, reason: 'tap' as StopReason }
    pendingMetaRef.current = null
    setTake(t)
    setTakeMeta({ mode: meta.mode, text: meta.text, para: meta.para })
    trackWriteSpeak('recording_completed', { mode: meta.mode, seconds: t.seconds, stop_reason: meta.reason, tier })
    if (canSendNow) startUpload(t)
    if (variant === 'locked' && upsellAutoEligible && !upsell.isOpen && !calloutDismissed(UPSELL_CALLOUT_KEY)) {
      upsell.open()
      trackWriteSpeak('pro_gate_hit', { feature: 'teacher_correction', trigger: 'auto', tier })
    }
  }

  const discardTake = () => {
    if (take) {
      try {
        URL.revokeObjectURL(take.url)
      } catch (e) {}
    }
    uploadRef.current = null
    setTake(null)
    setTakeMeta(null)
    // The spoken ticks belonged to that take: a new take starts from the writing's ticks only.
    setHeard({})
    setManual({})
  }

  const reRecord = () => {
    discardTake()
    recorderRef.current?.start()
  }

  // ---------- send ----------
  const [phase, setPhase] = useState<SendPhase>('idle')
  const [sendError, setSendError] = useState('')
  const [notice, setNotice] = useState('')
  const [capHitWithoutInfo, setCapHitWithoutInfo] = useState(false)
  const [sentSummary, setSentSummary] = useState<{
    writing: string
    recording: { mode: SpeakingModeId; seconds: number } | null
    used: string[]
  } | null>(null)

  // Editing after a save makes it saveable again.
  useEffect(() => {
    if (phase === 'saved' || phase === 'saved-not-sent') setPhase('idle')
  }, [answer, take])

  const answerChanged = hasText && !(variant === 'open' && trimmed === existingAnswer)
  const sendReady = answerChanged || (!!take && canSendNow)
  const saveReady = answerChanged

  const submit = async (send: boolean) => {
    if (phase === 'sending' || phase === 'saving') return
    setSendError('')
    const includeAnswer = answerChanged
    if (includeAnswer) {
      const check = await answerValidate(trimmed)
      if (check?.error) {
        setFieldError(check.error)
        textareaRef.current?.focus()
        return
      }
    }
    const includeRecording = send && !!take && canSendNow
    if (!includeAnswer && !includeRecording) return

    setPhase(send ? 'sending' : 'saving')
    const usedWritten = words.filter((w) => ticks[w.word]?.written).map((w) => w.word)
    const wordsHeard: WordHeard[] = hearsWords
      ? words.filter((w) => heard[w.word] !== undefined).map((w) => ({ word: w.word, atSeconds: heard[w.word] }))
      : []

    try {
      let recordingKey: string | undefined
      if (includeRecording && take) {
        try {
          recordingKey = await ensureUploaded(take)
        } catch {
          setPhase('idle')
          setSendError(t('step.errors.upload'))
          return
        }
      }
      const payload: WriteSpeakSubmitPayload = {
        storyId,
        level,
        memberName,
        send,
        practiceWords: words.map((w) => w.word),
        wordsWritten: usedWritten,
        ...(includeAnswer ? { answer: trimmed.replace(/[\n\r]/g, '') } : {}),
        ...(recordingKey && takeMeta
          ? {
              recordingKey,
              speakingMode: takeMeta.mode,
              ...(takeMeta.mode === 'read_passage' ? { passageIndex: takeMeta.para } : {}),
            }
          : {}),
        ...(wordsHeard.length ? { wordsHeard } : {}),
      }
      const [result] = await Promise.all([submitWriteSpeak(payload), wait(send && !reduced ? 450 : 0)])

      if (includeAnswer) {
        onAnswerSaved(trimmed)
        writeDraft(memberId, storyId, '')
        setHasSubmitted(true)
      }

      if (!send) {
        setPhase(variant === 'pro' ? 'saved-not-sent' : 'saved')
        return
      }
      if (result?.sent) {
        const used = words.filter((w) => isTicked(ticks[w.word])).map((w) => w.word)
        setSentSummary({
          writing: includeAnswer ? trimmed : existingAnswer,
          recording: includeRecording && takeMeta && take ? { mode: takeMeta.mode, seconds: take.seconds } : null,
          used,
        })
        setSent(true)
        setPhase('idle')
        hapticSuccess()
        trackWriteSpeak('correction_sent', {
          has_writing: includeAnswer,
          has_recording: includeRecording,
          mode: includeRecording && takeMeta ? takeMeta.mode : null,
          words_used: words.filter((w) => ticks[w.word]?.written || ticks[w.word]?.heard).length,
          words_ticked: used.length,
          words_total: words.length,
          tier: variant === 'taster' ? 'taster' : tier,
        })
        return
      }
      if (result?.dailyCorrectionAlreadyUsed) {
        setVariantOverride('pro-capped')
        setCapHitWithoutInfo(!wsState?.dailyCap)
        setPhase(includeAnswer ? 'saved' : 'idle')
        return
      }
      setPhase('idle')
      setSendError(t('step.errors.sendLater'))
    } catch (error: any) {
      const status = error?.response?.status
      if (status === 409) {
        if (includeAnswer) {
          onAnswerSaved(trimmed)
          writeDraft(memberId, storyId, '')
          setHasSubmitted(true)
        }
        setVariantOverride('corrected')
        setNotice(t('step.errors.alreadyCorrected'))
        setPhase('idle')
        return
      }
      setPhase('idle')
      setSendError(
        status && status < 500 ? apiErrorMessage(error, 'writeSpeak:step.errors.sendFailed') : t('step.errors.sendFailed'),
      )
    }
  }

  const onProTap = () => {
    if (upsell.isOpen) {
      upsell.dismiss()
      return
    }
    upsell.open()
    trackWriteSpeak('pro_gate_hit', { feature: 'teacher_correction', trigger: 'tap', tier })
  }

  // ---------- page-level effects ----------
  const hasUnsent = !!take && !sent && canSendNow
  // Block body: whatever the callback returns must not become the effect's cleanup.
  useEffect(() => {
    onUnsentChange(hasUnsent)
  }, [hasUnsent])

  const viewedRef = useRef(false)
  useEffect(() => {
    if (!wsStateLoaded || viewedRef.current) return
    viewedRef.current = true
    trackWriteSpeak('write_speak_viewed', {
      story_id: storyId,
      tier,
      variant,
      words_total: words.length,
      live_ticking: !!wsState?.liveTicking,
    })
  }, [wsStateLoaded])

  // Rail: held at the vertical centre of whatever scrolls the page. The centre is taken from
  // the rail's height when laid out; later growth (ticking the last word adds a line) only
  // updates --ws-rail-h, so the card grows downwards instead of creeping up.
  const bodyRef = useRef<HTMLDivElement>(null)
  const railRef = useRef<HTMLElement>(null)
  useLayoutEffect(() => {
    const rail = railRef.current
    const body = bodyRef.current
    if (!rail || !body) return
    const trackHeight = () => body.style.setProperty('--ws-rail-h', `${rail.offsetHeight}px`)
    const measure = () => {
      trackHeight()
      body.style.setProperty('--ws-rail-base-h', `${rail.offsetHeight}px`)
      body.style.setProperty('--ws-view-h', `${scrollParentHeight(body)}px`)
    }
    measure()
    window.addEventListener('resize', measure)
    document.fonts?.ready.then(measure)
    let ro: ResizeObserver | null = null
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(trackHeight)
      ro.observe(rail)
    }
    return () => {
      window.removeEventListener('resize', measure)
      ro?.disconnect()
    }
  }, [words.length])

  // The Back/Finish bar slides away while the keyboard is open on phones.
  const onFocus = () => {
    if (isPhone) document.body.classList.add('ws-typing')
  }
  const onBlur = () => {
    document.body.classList.remove('ws-typing')
    if (hasText) setWritingDone(true)
  }

  // ---------- render pieces ----------
  const hasWords = words.length > 0
  const wordsProps = {
    words,
    ticks,
    onToggle: onToggleWord,
    onInfo: onInfoWord,
    activeInfoWord,
    disabled: locked,
    characterPreference,
  }

  const left = words.length - tickedCount
  const sayHint = !hasWords ? null : left === 0 ? (
    t('step.sayHint.allUsed', { label: allWordsUsedLabel(words.length) })
  ) : liveOn ? (
    recording && mode === 'free_talk' ? (
      <>
        <span className='ws-live-dot' aria-hidden='true' />
        <Trans t={t} i18nKey='step.sayHint.listening' values={{ count: left }} components={{ b: <b /> }} />
      </>
    ) : (
      <Trans t={t} i18nKey='step.sayHint.live' values={{ count: left }} components={{ b: <b /> }} />
    )
  ) : (
    <Trans t={t} i18nKey='step.sayHint.manual' values={{ count: left }} components={{ b: <b /> }} />
  )

  const showNudge = !nudged && !reduced && writingDone && hasText && !take && !recording && !locked
  const showOthers = sent || hasSubmitted || !!curAnswer || !!existingAnswer
  const others = answers.filter((a) => a.id !== memberId)
  const othersTotal = Math.max(others.length, answersTotal - (answers.length !== others.length ? 1 : 0))

  const dailyCap = wsState?.dailyCap
  const capNotice =
    variant === 'pro-capped' && !sent ? (
      <div className='ws-notice' role='status'>
        <InfoIcon />
        {dailyCap && !capHitWithoutInfo ? (
          <div>
            <Trans
              t={t}
              i18nKey='step.cap.usedOn'
              values={{ time: localTime(dailyCap.nextOpensAt) }}
              components={{
                title: (
                  <b className='ws-inline-zh' lang={zhLang}>
                    {CAP_TITLE_OPEN}
                    <ZhWords
                      words={segmentText(dailyCap.usedOnStoryTitle || '', known)}
                      iterator='WS-CAP-TITLE-'
                      story={{}}
                      activeIndex={activeIndex}
                      onClickWord={onClickWord}
                      fontSize='inherit'
                      bold
                    />
                    {CAP_TITLE_CLOSE}
                  </b>
                ),
                time: <b />,
              }}
            />
          </div>
        ) : (
          <div>{t('step.cap.usedElsewhere')}</div>
        )}
      </div>
    ) : null

  const existingRecording = !!existing?.recordingUrl && (variant === 'open' || isFinal)
  const showRecorder = !isFinal && !existingRecording
  const showSpeakCard = !isFinal || existingRecording

  return (
    <div className='ws-step'>
      <div className={`ws-body${hasWords ? '' : ' no-rail'}`} ref={bodyRef}>
        <div className='ws-note'>{capNotice}</div>

        <div className='ws-q'>
          <div className='ws-label'>{t('step.todaysQuestion')}</div>
          <div className='ws-qline' lang={zhLang}>
            <ZhWords
              words={questionContent}
              iterator='QUESTION-TITLE'
              story={story}
              activeIndex={activeIndex}
              onClickWord={onClickWord}
              showPinYin={true}
              fontSize={isPhone ? '23px' : '27px'}
              pinyinSize='12px'
              bold
            />
          </div>
        </div>

        <section className={`ws-write${locked ? ' ws-is-locked' : ''}`} aria-labelledby='ws-write-title'>
          <div className='ws-card'>
            <div className='ws-card-head'>
              <span className='ws-step-dot' aria-hidden='true'>
                1
              </span>
              <div className='ws-card-head-grow'>
                <h4 id='ws-write-title'>{t('step.write.title')}</h4>
              </div>
              {variant === 'open' && !!existingAnswer && !editingExisting && !sent && (
                <button type='button' className='ws-inline-link' onClick={() => setEditingExisting(true)}>
                  {t('step.write.edit')}
                </button>
              )}
            </div>
            {hasWords && !writingReadOnly && (
              <div className='ws-rail-slot'>
                <WordsToTry {...wordsProps} />
              </div>
            )}
            {writingReadOnly ? (
              <div className='ws-readonly-answer' lang={zhLang}>
                {existingAnswer || trimmed || curAnswer}
              </div>
            ) : (
              <>
                <label htmlFor='ws-answer' className='ws-sr'>
                  {t('step.write.answerLabel')}
                </label>
                <textarea
                  id='ws-answer'
                  ref={textareaRef}
                  className={`ws-textarea${fieldError ? ' has-error' : ''}`}
                  lang={zhLang}
                  placeholder={ANSWER_PLACEHOLDER_ZH}
                  value={answer}
                  onChange={onAnswerChange}
                  onCompositionStart={() => (composingRef.current = true)}
                  onCompositionEnd={(e) => {
                    composingRef.current = false
                    if (debounceRef.current) clearTimeout(debounceRef.current)
                    setMatchText((e.target as HTMLTextAreaElement).value)
                  }}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  disabled={locked}
                  aria-invalid={!!fieldError}
                  aria-describedby={fieldError ? 'ws-answer-error' : undefined}
                />
                {hasWords && <TypingStrip words={words} ticks={ticks} characterPreference={characterPreference} />}
                {fieldError && (
                  <div className='ws-field-error' id='ws-answer-error' role='alert'>
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      <circle cx='12' cy='12' r='9' />
                      <path d='M12 8v5M12 16h.01' />
                    </svg>
                    {fieldError}
                  </div>
                )}
                <div className='ws-write-foot'>
                  <span className='ws-draft' aria-live='polite'>
                    {hasText && draftSaved && (
                      <>
                        <b aria-hidden='true' />
                        {t('step.write.draftSaved')}
                      </>
                    )}
                  </span>
                  <span>{t('step.write.characterCount', { count: countCharacters(answer) })}</span>
                </div>
                {wsState?.hasWrittenBefore === false && !hasSubmitted && (
                  <a className='ws-kbd' href={KEYBOARD_GUIDE_URL} target='_blank' rel='noopener noreferrer'>
                    <svg viewBox='0 0 24 24' aria-hidden='true'>
                      <rect x='2' y='6' width='20' height='12' rx='2' />
                      <path d='M6 10h.01M10 10h.01M14 10h.01M18 10h.01M8 14h8' />
                    </svg>
                    {t('step.write.keyboardGuide')}
                  </a>
                )}
              </>
            )}
          </div>
        </section>

        {showSpeakCard && (
          <section className={`ws-speak${locked ? ' ws-is-locked' : ''}`} aria-labelledby='ws-speak-title'>
            <div className='ws-card'>
              <div className='ws-card-head'>
                <span className='ws-step-dot' aria-hidden='true'>
                  2
                </span>
                <div className='ws-card-head-grow'>
                  <h4 id='ws-speak-title'>{t('step.speak.title')}</h4>
                </div>
              </div>
              {existingRecording ? (
                <div className='ws-sent-rec'>
                  <span className='ws-ic' aria-hidden='true'>
                    <svg viewBox='0 0 24 24'>
                      <rect x='9' y='3' width='6' height='11' rx='3' />
                      <path d='M5 11a7 7 0 0 0 14 0M12 18v3' />
                    </svg>
                  </span>
                  <span>
                    {variant === 'corrected' ? t('step.speak.recordingCorrected') : t('step.speak.recordingWithTeacher')}
                  </span>
                </div>
              ) : null}
              {showRecorder && (
                <>
                  <ModePicker
                    mode={mode}
                    onChange={setMode}
                    hasText={hasText}
                    holdsWritingTake={holdsWritingTake}
                    disabled={locked}
                  />
                  <Script
                    mode={mode}
                    story={story}
                    activeIndex={activeIndex}
                    onClickWord={onClickWord}
                    known={known}
                    answerText={trimmed}
                    frozenText={holdsWritingTake && takeMeta ? takeMeta.text : null}
                    onReRecord={reRecord}
                    questionContent={questionContent}
                    wordsMirror={hasWords ? <WordsList {...wordsProps} /> : null}
                    sayHint={sayHint}
                    paragraphs={paragraphs}
                    paragraphIndex={Math.min(para, Math.max(0, paragraphs.length - 1))}
                    onNextParagraph={() => setPara((p) => (paragraphs.length ? (p + 1) % paragraphs.length : 0))}
                  />
                  <StepRecorder
                    ref={recorderRef}
                    take={take}
                    takeLabel={t(MODE_TAKE_LABEL[takeMeta?.mode || mode])}
                    nudge={showNudge}
                    disabled={locked}
                    onStarted={onRecordingStarted}
                    onStopped={onRecordingStopped}
                    onTake={onTake}
                    onDiscard={discardTake}
                  />
                  {liveOn && hasWords && !allTicked && (
                    <p className='ws-live-note'>{t('step.speak.liveNote')}</p>
                  )}
                  {variant === 'locked' && (
                    <div className='ws-rec-note'>
                      <svg viewBox='0 0 24 24' aria-hidden='true'>
                        <rect x='4' y='10' width='16' height='11' rx='2' />
                        <path d='M8 10V7a4 4 0 0 1 8 0v3' />
                      </svg>
                      {t('step.speak.lockedNote')}
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        )}

        <div className='ws-send'>
          {sent && sentSummary ? (
            <Receipt
              isTaster={variant === 'taster'}
              wordsSaid={hearsWords}
              writing={sentSummary.writing}
              recording={sentSummary.recording}
              words={words}
              usedWords={sentSummary.used}
              characterPreference={characterPreference}
            />
          ) : wsStateLoaded ? (
            <>
              {notice && (
                <div className='ws-notice' role='status' style={{ marginBottom: 12 }}>
                  <InfoIcon />
                  <div>{notice}</div>
                </div>
              )}
              <SendArea
                variant={variant}
                ready={variant === 'locked' || variant === 'pro-capped' ? saveReady : sendReady}
                phase={phase}
                onSend={() => submit(true)}
                onSave={() => submit(false)}
                calloutOpen={upsell.isOpen}
                onProTap={onProTap}
                onCalloutDismiss={upsell.dismiss}
                error={sendError}
              />
            </>
          ) : null}
        </div>

        {hasWords && (
          <aside className='ws-rail' ref={railRef} aria-label={t('step.railLabel')}>
            <WordsToTry {...wordsProps} />
          </aside>
        )}

        <div className='ws-others'>
          {showOthers && renderOthers?.()}
          {showOthers && !renderOthers && (
            <OthersAnswers
              answers={others}
              total={othersTotal}
              loading={answersLoading}
              onLoadAll={onLoadAllAnswers}
              story={story}
              activeIndex={activeIndex}
              onClickWord={onClickWord}
            />
          )}
        </div>
        <div className='ws-end' aria-hidden='true' />
      </div>
    </div>
  )
}

export default WriteSpeakStep
